import { useMemo, useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import {
  hostedEvents,
  money,
  isBlank,
  toCents,
  toDollars,
  type ProgramStatus,
  type RebateConfig,
  type RebatePreview,
  type RebateProgram,
  type RebateRules,
} from '../../services/hostedEvents'
import { CHECK_LABELS, errorMessage } from './helpers'
import {
  ErrorBanner,
  Modal,
  Notice,
  StatusBadge,
  card,
  hintClass,
  inputClass,
  labelClass,
  sectionTitle,
} from './ui'

interface FormState {
  sponsor_name: string
  amount_mode: RebateRules['amount_mode']
  percent: string
  fixed: string
  basis: RebateRules['basis']
  cap: string
  budget: string
  max_recipients: string
  require_check_in: boolean
  min_sales_count: string
  min_sales: string
  sales_grace_hours: string
  include_offline_payments: boolean
  notes: string
}

const toForm = (rules: RebateRules): FormState => ({
  sponsor_name: rules.sponsor_name,
  amount_mode: rules.amount_mode,
  percent: rules.percent_bps === null ? '' : String(rules.percent_bps / 100),
  fixed: toDollars(rules.fixed_cents),
  basis: rules.basis,
  cap: toDollars(rules.per_vendor_cap_cents),
  budget: toDollars(rules.budget_cents),
  max_recipients: rules.max_recipients === null ? '' : String(rules.max_recipients),
  require_check_in: rules.require_check_in,
  min_sales_count: String(rules.min_sales_count),
  min_sales: toDollars(rules.min_sales_cents),
  sales_grace_hours: String(rules.sales_grace_hours),
  include_offline_payments: rules.include_offline_payments,
  notes: rules.notes,
})

function toRules(
  form: FormState,
  config: RebateConfig
): { rules?: RebateRules; problems: string[] } {
  const problems: string[] = []
  const whole = (text: string, label: string, optional = false) => {
    if (text.trim() === '' && optional) return null
    const value = Number(text)
    if (!Number.isInteger(value) || value < 0) problems.push(`${label} must be a whole number`)
    return value
  }
  const dollars = (text: string, label: string, max: number) => {
    if (isBlank(text)) return null
    const cents = toCents(text)
    if (cents === null) {
      problems.push(`${label} is not a valid dollar amount`)
      return null
    }
    if (cents <= 0) problems.push(`${label} must be more than $0`)
    if (cents > max) problems.push(`${label} cannot be more than ${money(max)}`)
    return cents
  }
  if (!form.sponsor_name.trim()) problems.push('Sponsor name is required')
  const percent = Number(form.percent)
  if (form.amount_mode === 'percent' && !(percent > 0 && percent <= 100))
    problems.push('Percent must be between 0.01 and 100')
  const fixed =
    form.amount_mode === 'fixed'
      ? dollars(form.fixed, 'Fixed amount', config.max_rebate_cents)
      : null
  if (form.amount_mode === 'fixed' && fixed === null) problems.push('Fixed amount is required')
  const grace = whole(form.sales_grace_hours, 'Grace hours') ?? 0
  if (grace > config.max_sales_grace_hours)
    problems.push(`Grace hours cannot be more than ${config.max_sales_grace_hours}`)
  const recipients = whole(form.max_recipients, 'Recipient limit', true)
  if (recipients === 0) problems.push('Recipient limit must be at least 1')
  const minSales = isBlank(form.min_sales) ? 0 : toCents(form.min_sales)
  if (minSales === null) problems.push('Minimum sales value is not a valid dollar amount')
  const rules: RebateRules = {
    sponsor_name: form.sponsor_name.trim(),
    amount_mode: form.amount_mode,
    percent_bps: form.amount_mode === 'percent' ? Math.round(percent * 100) : null,
    fixed_cents: fixed,
    basis: form.basis,
    per_vendor_cap_cents: dollars(form.cap, 'Per-vendor cap', config.max_rebate_cents),
    budget_cents: dollars(form.budget, 'Budget', config.max_budget_cents),
    max_recipients: recipients,
    require_check_in: form.require_check_in,
    min_sales_count: whole(form.min_sales_count, 'Minimum sales') ?? 0,
    min_sales_cents: minSales ?? 0,
    sales_grace_hours: grace,
    include_offline_payments: form.include_offline_payments,
    notes: form.notes,
  }
  return problems.length ? { problems } : { rules, problems }
}

const STATUS_ACTIONS: Record<
  ProgramStatus,
  { to: ProgramStatus; label: string; variant: 'success' | 'secondary' | 'danger' }[]
> = {
  draft: [{ to: 'active', label: 'Activate', variant: 'success' }],
  active: [
    { to: 'paused', label: 'Pause', variant: 'secondary' },
    { to: 'closed', label: 'Close program', variant: 'danger' },
  ],
  paused: [
    { to: 'active', label: 'Resume', variant: 'success' },
    { to: 'closed', label: 'Close program', variant: 'danger' },
  ],
  closed: [{ to: 'paused', label: 'Reopen as paused', variant: 'secondary' }],
}

const STATUS_HELP: Record<ProgramStatus, string> = {
  draft:
    'Draft: rules can be edited freely. No rebates can be opened until the program is activated.',
  active:
    'Active: rebates can be opened and approved. Rule changes apply to new rebates; refresh pending ones from the Vendors tab.',
  paused: 'Paused: nothing new can be opened or approved. Approved rebates can still be paid.',
  closed:
    'Closed: the program is finished. Approved rebates can still be paid. Reopen it to edit rules.',
}

export function RebateProgramPanel({
  marketId,
  program,
  config,
  vendorsPay,
  onChanged,
}: {
  marketId: string
  program: RebateProgram | null
  config: RebateConfig
  vendorsPay: boolean
  onChanged: () => void
}) {
  const canConfigure = usePermission('event_rebates', 'configure')
  const [form, setForm] = useState<FormState>(() => toForm(program || config.defaults))
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState<RebatePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [statusTarget, setStatusTarget] = useState<ProgramStatus | null>(null)
  const [reason, setReason] = useState('')

  const locked = !canConfigure || program?.status === 'closed'
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(toForm(program || config.defaults)),
    [form, program, config]
  )

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setSaved(false)
    setPreview(null)
    setForm((current) => ({ ...current, [key]: value }))
  }

  const parsed = () => {
    const result = toRules(form, config)
    if (!result.rules) setError(result.problems.join('. '))
    return result.rules
  }

  const runPreview = async () => {
    const rules = parsed()
    if (!rules) return
    setPreviewing(true)
    setError(null)
    try {
      setPreview(await hostedEvents.preview(marketId, rules))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setPreviewing(false)
    }
  }

  const save = async () => {
    const rules = parsed()
    if (!rules) return
    setSaving(true)
    setError(null)
    try {
      await hostedEvents.saveProgram(marketId, rules, program ? program.revision : null)
      setSaved(true)
      onChanged()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSaving(false)
    }
  }

  const changeStatus = async () => {
    if (!program || !statusTarget) return
    setSaving(true)
    setError(null)
    try {
      await hostedEvents.setProgramStatus(marketId, statusTarget, program.revision, reason.trim())
      setStatusTarget(null)
      setReason('')
      onChanged()
    } catch (caught) {
      setError(errorMessage(caught))
      setStatusTarget(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {error && <ErrorBanner message={error} />}
      {!vendorsPay && (
        <Notice tone="amber">
          Vendors do not pay a booth fee at this event, so nobody will qualify for a rebate under
          the rules.
        </Notice>
      )}

      <div className={`${card} p-5`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className={sectionTitle}>Program status</h3>
            {program ? <StatusBadge status={program.status} /> : <StatusBadge status="draft" />}
          </div>
          {program && canConfigure && (
            <div className="flex gap-2">
              {STATUS_ACTIONS[program.status].map((action) => (
                <CustomButton
                  key={action.to}
                  size="sm"
                  variant={action.variant}
                  disabled={dirty}
                  title={dirty ? 'Save or discard rule changes first' : undefined}
                  onClick={() => setStatusTarget(action.to)}
                >
                  {action.label}
                </CustomButton>
              ))}
            </div>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {program
            ? STATUS_HELP[program.status]
            : 'No rebate program yet. Set the rules below and save to create a draft.'}
        </p>
      </div>

      <fieldset disabled={locked} className={`${card} space-y-6 p-5`}>
        <div>
          <h3 className={sectionTitle}>How much vendors get back</h3>
          <div className="mt-3 grid gap-4 md:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="rebate-mode">
                Amount
              </label>
              <select
                id="rebate-mode"
                className={inputClass}
                value={form.amount_mode}
                onChange={(event) =>
                  set('amount_mode', event.target.value as FormState['amount_mode'])
                }
              >
                <option value="full_fee">Full booth fee</option>
                <option value="percent">Percent of booth fee</option>
                <option value="fixed">Fixed amount</option>
              </select>
            </div>
            {form.amount_mode === 'percent' && (
              <div>
                <label className={labelClass} htmlFor="rebate-percent">
                  Percent
                </label>
                <input
                  id="rebate-percent"
                  className={inputClass}
                  inputMode="decimal"
                  value={form.percent}
                  onChange={(event) => set('percent', event.target.value)}
                  placeholder="50"
                />
              </div>
            )}
            {form.amount_mode === 'fixed' && (
              <div>
                <label className={labelClass} htmlFor="rebate-fixed">
                  Fixed amount ($)
                </label>
                <input
                  id="rebate-fixed"
                  className={inputClass}
                  inputMode="decimal"
                  value={form.fixed}
                  onChange={(event) => set('fixed', event.target.value)}
                  placeholder="75.00"
                />
                <p className={hintClass}>Never more than the vendor actually paid.</p>
              </div>
            )}
            <div>
              <label className={labelClass} htmlFor="rebate-basis">
                Based on
              </label>
              <select
                id="rebate-basis"
                className={inputClass}
                value={form.basis}
                onChange={(event) => set('basis', event.target.value as FormState['basis'])}
              >
                <option value="booth_price">Booth price</option>
                <option value="total_paid">Total paid, including Barta fees</option>
              </select>
              <p className={hintClass}>Any booth refund is subtracted first.</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="rebate-cap">
                Per-vendor cap ($)
              </label>
              <input
                id="rebate-cap"
                className={inputClass}
                inputMode="decimal"
                value={form.cap}
                onChange={(event) => set('cap', event.target.value)}
                placeholder="No cap"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className={sectionTitle}>Who qualifies</h3>
          <div className="mt-3 grid gap-4 md:grid-cols-3">
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.require_check_in}
                onChange={(event) => set('require_check_in', event.target.checked)}
              />
              <span>
                Must be checked in at the event
                <span className={`block ${hintClass}`}>Uses the organizer's vendor check-in.</span>
              </span>
            </label>
            <div>
              <label className={labelClass} htmlFor="rebate-sales">
                Minimum in-person sales
              </label>
              <input
                id="rebate-sales"
                className={inputClass}
                inputMode="numeric"
                value={form.min_sales_count}
                onChange={(event) => set('min_sales_count', event.target.value)}
              />
              <p className={hintClass}>
                Paid sales through Barta during the event. 0 turns this off.
              </p>
            </div>
            <div>
              <label className={labelClass} htmlFor="rebate-sales-value">
                Minimum sales value ($)
              </label>
              <input
                id="rebate-sales-value"
                className={inputClass}
                inputMode="decimal"
                value={form.min_sales}
                onChange={(event) => set('min_sales', event.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="rebate-grace">
                Sales grace window (hours)
              </label>
              <input
                id="rebate-grace"
                className={inputClass}
                inputMode="numeric"
                value={form.sales_grace_hours}
                onChange={(event) => set('sales_grace_hours', event.target.value)}
              />
              <p className={hintClass}>
                Counts sales this long before the start and after the end. Up to{' '}
                {config.max_sales_grace_hours}.
              </p>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 md:col-span-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.include_offline_payments}
                onChange={(event) => set('include_offline_payments', event.target.checked)}
              />
              <span>
                Include vendors who paid the organizer offline
                <span className={`block ${hintClass}`}>
                  Barta never saw that money. The amount is whatever the organizer typed in.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div>
          <h3 className={sectionTitle}>Funding</h3>
          <div className="mt-3 grid gap-4 md:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="rebate-sponsor">
                Sponsor
              </label>
              <input
                id="rebate-sponsor"
                className={inputClass}
                value={form.sponsor_name}
                maxLength={120}
                onChange={(event) => set('sponsor_name', event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="rebate-budget">
                Total budget ($)
              </label>
              <input
                id="rebate-budget"
                className={inputClass}
                inputMode="decimal"
                value={form.budget}
                onChange={(event) => set('budget', event.target.value)}
                placeholder="No limit"
              />
              <p className={hintClass}>Approvals that would go over are refused.</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="rebate-recipients">
                Recipient limit
              </label>
              <input
                id="rebate-recipients"
                className={inputClass}
                inputMode="numeric"
                value={form.max_recipients}
                onChange={(event) => set('max_recipients', event.target.value)}
                placeholder="No limit"
              />
            </div>
            <div className="md:col-span-3">
              <label className={labelClass} htmlFor="rebate-notes">
                Internal notes
              </label>
              <textarea
                id="rebate-notes"
                className={inputClass}
                rows={3}
                maxLength={2000}
                value={form.notes}
                onChange={(event) => set('notes', event.target.value)}
                placeholder="Why this event has a rebate, who agreed to fund it, anything the next reviewer should know."
              />
            </div>
          </div>
        </div>
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <CustomButton variant="secondary" onClick={runPreview} loading={previewing}>
          Preview against current vendors
        </CustomButton>
        {canConfigure && program?.status !== 'closed' && (
          <CustomButton onClick={save} loading={saving} disabled={!!program && !dirty}>
            {program ? 'Save rules' : 'Create draft program'}
          </CustomButton>
        )}
        {dirty && program && (
          <CustomButton variant="ghost" onClick={() => setForm(toForm(program))}>
            Discard changes
          </CustomButton>
        )}
        {saved && !dirty && (
          <span className="text-sm text-emerald-700 dark:text-emerald-300">Saved.</span>
        )}
        {!canConfigure && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            You can view and preview rules but not change them.
          </span>
        )}
      </div>

      {preview && (
        <div className={`${card} p-5`}>
          <h3 className={sectionTitle}>Preview</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Nothing is saved. This is what these rules would do with today's check-ins and sales.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Paid vendors</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {preview.paid_vendors}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Would qualify</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {preview.eligible_count}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">
                Cost if all approved
              </div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {money(preview.projected_cents)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Fits the limits</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {preview.funded_count} · {money(preview.funded_cents)}
              </div>
            </div>
          </div>
          {preview.over_budget && (
            <div className="mt-4">
              <Notice tone="amber">
                The budget or recipient limit does not cover everyone who qualifies.{' '}
                {preview.eligible_count - preview.funded_count} vendor(s) would be left out.
              </Notice>
            </div>
          )}
          {Object.keys(preview.failed_checks).length > 0 && (
            <div className="mt-4 text-sm text-gray-700 dark:text-gray-300">
              <div className="font-medium">Why vendors miss out</div>
              <ul className="mt-1 list-inside list-disc text-gray-600 dark:text-gray-400">
                {Object.entries(preview.failed_checks).map(([key, count]) => (
                  <li key={key}>
                    {CHECK_LABELS[key] || key}: {count}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {statusTarget && program && (
        <Modal title={`Change program to ${statusTarget}`} onClose={() => setStatusTarget(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-400">{STATUS_HELP[statusTarget]}</p>
          <label className={`${labelClass} mt-4`} htmlFor="status-reason">
            Reason (recorded in the audit log)
          </label>
          <textarea
            id="status-reason"
            className={inputClass}
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="mt-4 flex justify-end gap-2">
            <CustomButton variant="secondary" onClick={() => setStatusTarget(null)}>
              Cancel
            </CustomButton>
            <CustomButton onClick={changeStatus} loading={saving} disabled={!reason.trim()}>
              Confirm
            </CustomButton>
          </div>
        </Modal>
      )}
    </div>
  )
}
